// Shared constants for the identity-cookie proxy. Ported from
// @eventbrite/webapp-cookies-middleware (valkyrie-utils) and eb-web edge so the
// proxy reads and re-issues the exact same cookie/header shapes — any drift
// silently resets users' guest/session identity or re-buckets their stableId.

import { Stage } from '@eventbrite/webapp-constants';

/**
 * Deployment stage. Re-exported from @eventbrite/webapp-constants (not
 * re-declared locally) so every module in apps/web that needs a Stage type —
 * including generated per-backend SOA clients under shared/clients/ whose
 * signatures (e.g. buildEventServiceClient(stage)) are hard-coded to this
 * package's Stage — shares one canonical enum instead of two nominally
 * distinct ones with identical values, which previously needed an `as
 * unknown as` cast at every generated-client call site to bridge them.
 */
export { Stage };

/**
 * Resolve the deployment stage from the STAGE env var, read at call time.
 * Returns undefined when STAGE is unset or not a recognized value — we never
 * guess a stage, because guessing would read another environment's signing key.
 * The caller treats "no stage" as "don't fetch the secret" and fails open.
 */
export function resolveStage(): Stage | undefined {
  switch (process.env.STAGE?.toLowerCase()) {
    case 'local':
      return Stage.LOCAL;
    case 'test':
      return Stage.TEST;
    case 'prod':
      return Stage.PROD;
    default:
      return undefined;
  }
}

// --- Cookie keys (as stored in the browser) ---
export const GUEST_COOKIE_KEY = 'guest';
export const SESSION_COOKIE_KEY = 'session';
export const ORGANIZATION_COOKIE_KEY = 'active_org_id';
export const STABLE_ID_COOKIE_KEY = 'stableId';
export const CSRF_COOKIE_KEY = 'csrftoken';

// --- Request headers injected for the downstream app (lowercase; mirror
//     @eventbrite/webapp-constants EventbriteHeader) ---
export const EB_GUEST_ID = 'eb-guest-id';
export const EB_SESSION_ID = 'eb-session-id';
export const EB_ORGANIZATION_ID = 'eb-organization-id';
export const EB_STABLE_ID = 'eb-stable-id';
export const EB_CORRELATION_ID = 'eb-correlation-id';
export const EB_HOST = 'eb-host';
export const EB_USER_ID = 'eb-user-id';
export const EB_SOA_TOKEN = 'eb-soa-token';
export const EB_LANG = 'eb-lang';
export const EB_LOCATION_SLUG = 'eb-location-slug';
export const X_EB_ORIGIN_KEY = 'x-eb-origin-key';
export const X_EB_ORIGIN_BUCKET = 'x-eb-origin-bucket';
export const X_EB_ORIGIN_ID = 'x-eb-origin-id';

/**
 * The stableId the CloudFront edge (viewer-request) already resolved for this
 * request — cookie -> ?stableId= override -> generate — and used to bucket /
 * route. Distinct from the app-facing `eb-stable-id`: this is the edge's input
 * handoff. The proxy MUST prefer it so the origin uses the same id the edge
 * bucketed on (the stableId cookie isn't set until viewer-response, so on a
 * first request it is the only signal that agrees with the edge's routing).
 * The edge overwrites this header unconditionally, so a client cannot spoof it
 * through CloudFront.
 */
export const X_EB_STABLE_ID = 'x-eb-stable-id';

export function isProxyManagedHeader(name: string): boolean {
  const normalizedName = name.toLowerCase();
  return (
    normalizedName.startsWith('eb-') ||
    normalizedName.startsWith('x-eb-') ||
    normalizedName.startsWith('x-amzn-') ||
    normalizedName.startsWith('x-amz-cf-') ||
    normalizedName.startsWith('cloudfront-') ||
    normalizedName === 'authorization' ||
    normalizedName === 'cookie' ||
    normalizedName === 'forwarded' ||
    normalizedName.startsWith('x-forwarded-')
  );
}

/** Sliding session window, in seconds (3h). Mirrors valkyrie SessionCookie. */
export const MAX_SESSION_AGE = 10800;

/** Guest cookie lifetime, in ms (~1 year). Mirrors valkyrie guest cookieData. */
export const GUEST_COOKIE_MAX_AGE_MS = 365 * 24 * 60 * 60 * 1000;

// --- stableId cookie format (mirror eb-web edge/config.ts so issuance is
//     identical across the CloudFront edge and this proxy) ---
export const STABLE_ID_MAX_AGE = 31363200; // ~364 days in seconds
export const KNOWN_COOKIE_DOMAIN_ROOTS = ['eventbrite', 'evbqa'] as const;

export const CSRF_TOKEN_MAX_AGE = 31449600;
